import React, { useState, useEffect } from 'react';
import { Filter, X, ChevronDown, Calendar as CalendarIcon, Check, RotateCcw } from 'lucide-react';

// Filter Badge Component
function FilterBadge({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 text-neutral-700 rounded-full text-sm">
      {label}
      <button onClick={onRemove} className="hover:text-red-500 transition-colors">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

// Multi-Select Dropdown Component
function MultiSelectDropdown({ label, options, selected, onChange, placeholder, accentColor = 'pink' }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const colorClasses = {
    pink: { bg: 'bg-pink-500', hover: 'hover:bg-pink-50', ring: 'ring-pink-500', text: 'text-pink-600' },
    yellow: { bg: 'bg-yellow-500', hover: 'hover:bg-yellow-50', ring: 'ring-yellow-500', text: 'text-yellow-600' },
    blue: { bg: 'bg-blue-500', hover: 'hover:bg-blue-50', ring: 'ring-blue-500', text: 'text-blue-600' }
  };
  const colors = colorClasses[accentColor] || colorClasses.pink;
  
  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 text-left border border-white/40 rounded-xl bg-white flex items-center justify-between ${isOpen ? `ring-2 ${colors.ring}` : ''}`}
      >
        <span className={selected.length > 0 ? 'text-[#1a1a2e]' : 'text-neutral-400'}>
          {selected.length > 0 ? `${selected.length} selected` : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setIsOpen(false)} />
          <div className="absolute z-[70] w-full mt-1 glass-card rounded-xl shadow-lg max-h-60 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-neutral-500 text-sm">No options available</div>
            ) : (
              options.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleOption(opt.value)}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${colors.hover} ${selected.includes(opt.value) ? 'bg-white/40' : ''}`}
                >
                  <span className={`w-4 h-4 border rounded flex items-center justify-center ${selected.includes(opt.value) ? `${colors.bg} border-transparent` : 'border-white/40'}`}>
                    {selected.includes(opt.value) && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="flex-1">{opt.label}</span>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Single Select Dropdown
function SingleSelectDropdown({ label, options, value, onChange, placeholder, accentColor = 'pink' }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const colorClasses = {
    pink: { ring: 'ring-pink-500', hover: 'hover:bg-pink-50', text: 'text-pink-600' },
    yellow: { ring: 'ring-yellow-500', hover: 'hover:bg-yellow-50', text: 'text-yellow-600' },
    blue: { ring: 'ring-blue-500', hover: 'hover:bg-blue-50', text: 'text-blue-600' }
  };
  const colors = colorClasses[accentColor] || colorClasses.pink;
  
  const selectedOption = options.find(o => o.value === value);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 text-left border border-white/40 rounded-xl bg-white flex items-center justify-between ${isOpen ? `ring-2 ${colors.ring}` : ''}`}
      >
        <span className={value ? 'text-[#1a1a2e]' : 'text-neutral-400'}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setIsOpen(false)} />
          <div className="absolute z-[70] w-full mt-1 glass-card rounded-xl shadow-lg max-h-60 overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange(''); setIsOpen(false); }}
              className={`w-full px-3 py-2 text-left text-sm ${colors.hover} text-neutral-400`}
            >
              {placeholder}
            </button>
            {options.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { onChange(opt.value); setIsOpen(false); }}
                className={`w-full px-3 py-2 text-left text-sm ${colors.hover} ${value === opt.value ? 'bg-white/40 font-medium' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Main Task Filters Component
export default function TaskFilters({ 
  filters, 
  onFiltersChange, 
  clients = [], 
  departments = [], 
  employees = [], 
  taskTypes = [],
  showClientFilter = true,
  accentColor = 'pink',
  isMobile = false
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  
  const colorClasses = {
    pink: { button: 'from-[#FF3366] to-[#FF3366]', badge: 'bg-pink-100 text-pink-700', outline: 'border-pink-500 text-pink-600' },
    yellow: { button: 'from-yellow-500 to-yellow-600', badge: 'bg-yellow-100 text-yellow-700', outline: 'border-yellow-500 text-yellow-600' },
    blue: { button: 'from-[#3366FF] to-[#3366FF]', badge: 'bg-blue-100 text-blue-700', outline: 'border-blue-500 text-blue-600' }
  };
  const colors = colorClasses[accentColor] || colorClasses.pink;

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const statusOptions = [
    { value: 'New', label: 'New' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Review', label: 'Review' },
    { value: 'Revision', label: 'Revision' },
    { value: 'Completed', label: 'Completed' }
  ];

  const priorityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Critical', label: 'Critical' }
  ];

  const clientOptions = clients.map(c => ({ value: c._id, label: c.name }));
  // Departments in tasks are stored as NAMES, not ObjectIds
  const departmentOptions = departments.map(d => ({ value: d.name, label: d.name }));
  const employeeOptions = employees.map(e => ({ value: e._id, label: e.personal?.full_name || e.work_email || 'Unknown' }));
  const typeOptions = taskTypes.map(t => ({ value: t.name, label: t.name }));
  
  // Get subtypes based on selected types
  const getSubtypeOptions = () => {
    if (localFilters.taskTypes.length === 0) {
      return taskTypes.flatMap(t => (t.subtypes || []).map(s => ({ value: s, label: s })));
    }
    return taskTypes
      .filter(t => localFilters.taskTypes.includes(t.name))
      .flatMap(t => (t.subtypes || []).map(s => ({ value: s, label: s })));
  };

  const activeFilterCount = () => {
    let count = 0;
    if (localFilters.clients?.length > 0) count++;
    if (localFilters.departments?.length > 0) count++;
    if (localFilters.employees?.length > 0) count++;
    if (localFilters.taskTypes?.length > 0) count++;
    if (localFilters.subtypes?.length > 0) count++;
    if (localFilters.statuses?.length > 0) count++;
    if (localFilters.priorities?.length > 0) count++;
    if (localFilters.dateFrom || localFilters.dateTo) count++;
    return count;
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    setIsModalOpen(false);
  };

  const handleReset = () => {
    const resetFilters = {
      clients: [],
      departments: [],
      employees: [],
      taskTypes: [],
      subtypes: [],
      statuses: [],
      priorities: [],
      dateFrom: '',
      dateTo: ''
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
    setIsModalOpen(false);
  };

  const removeFilter = (type, value) => {
    const newFilters = { ...filters };
    if (type === 'dateFrom' || type === 'dateTo') {
      newFilters[type] = '';
    } else {
      newFilters[type] = filters[type].filter(v => v !== value);
    }
    onFiltersChange(newFilters);
  };

  const getFilterLabel = (type, value) => {
    switch (type) {
      case 'clients':
        return clients.find(c => c._id === value)?.name || value;
      case 'departments':
        // Departments are stored as names, so value IS the name
        return value;
      case 'employees':
        const emp = employees.find(e => e._id === value);
        return emp?.personal?.full_name || emp?.work_email || value;
      default:
        return value;
    }
  };

  const hasActiveFilters = activeFilterCount() > 0;

  // Mobile-only button view
  if (isMobile) {
    return (
      <>
        <button
          onClick={() => setIsModalOpen(true)}
          className={`px-3 py-2.5 border rounded-xl flex items-center gap-1.5 transition-colors ${hasActiveFilters ? `${colors.outline} bg-white` : 'border-white/40 text-neutral-700 hover:bg-white/60'}`}
        >
          <Filter className="w-4 h-4" />
          {hasActiveFilters && (
            <span className={`${colors.badge} px-1.5 py-0.5 rounded-full text-xs font-medium`}>
              {activeFilterCount()}
            </span>
          )}
        </button>

        {/* Filter Modal - Mobile optimized */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-[100] flex items-end justify-center">
            <div className="bg-white rounded-t-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/40 flex-shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-[#1a1a2e]">Filter Tasks</h2>
                  <p className="text-xs text-neutral-500 mt-0.5">Refine your task list</p>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-xl">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                {/* Date Range */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">From</label>
                    <input type="date" value={localFilters.dateFrom || ''} onChange={(e) => setLocalFilters({...localFilters, dateFrom: e.target.value})} 
                      className="w-full px-3 py-2 text-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">To</label>
                    <input type="date" value={localFilters.dateTo || ''} onChange={(e) => setLocalFilters({...localFilters, dateTo: e.target.value})} 
                      className="w-full px-3 py-2 text-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500" />
                  </div>
                </div>

                {showClientFilter && (
                  <MultiSelectDropdown label="Client" options={clientOptions} selected={localFilters.clients || []} onChange={(v) => setLocalFilters({...localFilters, clients: v})} placeholder="Select clients" accentColor={accentColor} />
                )}
                <MultiSelectDropdown label="Department" options={departmentOptions} selected={localFilters.departments || []} onChange={(v) => setLocalFilters({...localFilters, departments: v})} placeholder="Select departments" accentColor={accentColor} />
                <MultiSelectDropdown label="Assignee" options={employeeOptions} selected={localFilters.employees || []} onChange={(v) => setLocalFilters({...localFilters, employees: v})} placeholder="Select assignees" accentColor={accentColor} />
                <MultiSelectDropdown label="Task Type" options={typeOptions} selected={localFilters.taskTypes || []} onChange={(v) => setLocalFilters({...localFilters, taskTypes: v, subtypes: []})} placeholder="Select types" accentColor={accentColor} />
                {getSubtypeOptions().length > 0 && (
                  <MultiSelectDropdown label="Subtype" options={getSubtypeOptions()} selected={localFilters.subtypes || []} onChange={(v) => setLocalFilters({...localFilters, subtypes: v})} placeholder="Select subtypes" accentColor={accentColor} />
                )}
                <MultiSelectDropdown label="Status" options={statusOptions} selected={localFilters.statuses || []} onChange={(v) => setLocalFilters({...localFilters, statuses: v})} placeholder="Select statuses" accentColor={accentColor} />
                <MultiSelectDropdown label="Priority" options={priorityOptions} selected={localFilters.priorities || []} onChange={(v) => setLocalFilters({...localFilters, priorities: v})} placeholder="Select priorities" accentColor={accentColor} />
              </div>

              {/* Footer */}
              <div className="flex items-center gap-3 p-4 border-t border-white/40 bg-white/40 flex-shrink-0">
                <button onClick={handleReset} className="flex-1 px-4 py-3 border border-white/40 rounded-xl text-neutral-700 hover:bg-neutral-100 font-medium text-sm">
                  Reset All
                </button>
                <button onClick={handleApply} className={`flex-1 px-4 py-3 bg-gradient-to-r ${colors.button} text-white rounded-xl font-medium text-sm`}>
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Filter Button */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setIsModalOpen(true)}
          className={`px-4 py-2 border rounded-xl flex items-center gap-2 transition-colors ${hasActiveFilters ? `${colors.outline} bg-white` : 'border-white/40 text-neutral-700 hover:bg-white/60'}`}
        >
          <Filter className="w-4 h-4" />
          Filters
          {hasActiveFilters && (
            <span className={`${colors.badge} px-2 py-0.5 rounded-full text-xs font-medium`}>
              {activeFilterCount()}
            </span>
          )}
        </button>
        
        {hasActiveFilters && (
          <button
            onClick={handleReset}
            className="px-3 py-2 text-neutral-500 hover:text-neutral-700 flex items-center gap-1 text-sm"
          >
            <RotateCcw className="w-3 h-3" />
            Clear all
          </button>
        )}
      </div>

      {/* Active Filter Badges */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {filters.dateFrom && (
            <FilterBadge label={`From: ${filters.dateFrom}`} onRemove={() => removeFilter('dateFrom')} />
          )}
          {filters.dateTo && (
            <FilterBadge label={`To: ${filters.dateTo}`} onRemove={() => removeFilter('dateTo')} />
          )}
          {showClientFilter && filters.clients?.map(v => (
            <FilterBadge key={`client-${v}`} label={`Client: ${getFilterLabel('clients', v)}`} onRemove={() => removeFilter('clients', v)} />
          ))}
          {filters.departments?.map(v => (
            <FilterBadge key={`dept-${v}`} label={`Dept: ${getFilterLabel('departments', v)}`} onRemove={() => removeFilter('departments', v)} />
          ))}
          {filters.employees?.map(v => (
            <FilterBadge key={`emp-${v}`} label={`Assignee: ${getFilterLabel('employees', v)}`} onRemove={() => removeFilter('employees', v)} />
          ))}
          {filters.taskTypes?.map(v => (
            <FilterBadge key={`type-${v}`} label={`Type: ${v}`} onRemove={() => removeFilter('taskTypes', v)} />
          ))}
          {filters.subtypes?.map(v => (
            <FilterBadge key={`subtype-${v}`} label={`Subtype: ${v}`} onRemove={() => removeFilter('subtypes', v)} />
          ))}
          {filters.statuses?.map(v => (
            <FilterBadge key={`status-${v}`} label={`Status: ${v}`} onRemove={() => removeFilter('statuses', v)} />
          ))}
          {filters.priorities?.map(v => (
            <FilterBadge key={`priority-${v}`} label={`Priority: ${v}`} onRemove={() => removeFilter('priorities', v)} />
          ))}
        </div>
      )}

      {/* Filter Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/40">
              <div>
                <h2 className="text-xl font-bold text-[#1a1a2e]">Filter Tasks</h2>
                <p className="text-sm text-neutral-500 mt-1">Combine multiple filters to refine your task list</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-xl">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Filter Content */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Date Range */}
              <div>
                <h3 className="text-sm font-semibold text-[#1a1a2e] mb-3 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  Date Range
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">From</label>
                    <input
                      type="date"
                      value={localFilters.dateFrom || ''}
                      onChange={(e) => setLocalFilters({ ...localFilters, dateFrom: e.target.value })}
                      className="w-full px-3 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">To</label>
                    <input
                      type="date"
                      value={localFilters.dateTo || ''}
                      onChange={(e) => setLocalFilters({ ...localFilters, dateTo: e.target.value })}
                      className="w-full px-3 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
                    />
                  </div>
                </div>
              </div>

              {/* Client & Department */}
              {(showClientFilter || departments.length > 0) && (
                <div className="grid grid-cols-2 gap-4">
                  {showClientFilter && (
                    <MultiSelectDropdown
                      label="Client"
                      options={clientOptions}
                      selected={localFilters.clients || []}
                      onChange={(v) => setLocalFilters({ ...localFilters, clients: v })}
                      placeholder="All clients"
                      accentColor={accentColor}
                    />
                  )}
                  <MultiSelectDropdown
                    label="Department"
                    options={departmentOptions}
                    selected={localFilters.departments || []}
                    onChange={(v) => setLocalFilters({ ...localFilters, departments: v })}
                    placeholder="All departments"
                    accentColor={accentColor}
                  />
                </div>
              )}

              {/* Assignee */}
              <MultiSelectDropdown
                label="Assignee"
                options={employeeOptions}
                selected={localFilters.employees || []}
                onChange={(v) => setLocalFilters({ ...localFilters, employees: v })}
                placeholder="All assignees"
                accentColor={accentColor}
              />

              {/* Task Type & Subtype */}
              <div className="grid grid-cols-2 gap-4">
                <MultiSelectDropdown
                  label="Task Type"
                  options={typeOptions}
                  selected={localFilters.taskTypes || []}
                  onChange={(v) => setLocalFilters({ ...localFilters, taskTypes: v, subtypes: [] })}
                  placeholder="All types"
                  accentColor={accentColor}
                />
                <MultiSelectDropdown
                  label="Subtype"
                  options={getSubtypeOptions()}
                  selected={localFilters.subtypes || []}
                  onChange={(v) => setLocalFilters({ ...localFilters, subtypes: v })}
                  placeholder="All subtypes"
                  accentColor={accentColor}
                />
              </div>

              {/* Status & Priority */}
              <div className="grid grid-cols-2 gap-4">
                <MultiSelectDropdown
                  label="Status"
                  options={statusOptions}
                  selected={localFilters.statuses || []}
                  onChange={(v) => setLocalFilters({ ...localFilters, statuses: v })}
                  placeholder="All statuses"
                  accentColor={accentColor}
                />
                <MultiSelectDropdown
                  label="Priority"
                  options={priorityOptions}
                  selected={localFilters.priorities || []}
                  onChange={(v) => setLocalFilters({ ...localFilters, priorities: v })}
                  placeholder="All priorities"
                  accentColor={accentColor}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-white/40 bg-white/40">
              <button
                onClick={handleReset}
                className="px-4 py-2 text-neutral-600 hover:text-[#1a1a2e] flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Reset All
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-2 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  className={`px-6 py-2 bg-gradient-to-r ${colors.button} text-white rounded-xl hover:opacity-90`}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
